import React, { useEffect, useState } from "react";
import { AlertTriangle, ShieldCheck, X, Wallet, MessageSquare, HelpCircle, GraduationCap, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const ACCEPT_KEY = "tradingox_risk_ack_v1";

export const RISK_HEADLINE = "Trading is highly risky. Only trade money you can afford to lose.";

export const RiskBanner = ({ onOpen, testid = "risk-banner" }) => (
  <div
    data-testid={testid}
    className="w-full border-y border-amber-500/30 bg-amber-500/10 text-amber-100"
  >
    <div className="max-w-[1600px] mx-auto px-4 lg:px-8 py-2 flex items-center gap-3 text-xs">
      <AlertTriangle className="w-4 h-4 shrink-0 text-amber-300" />
      <div className="truncate">
        <span className="font-semibold">Risk warning:</span> {RISK_HEADLINE} Do not borrow money to trade. Do not use your life savings. TradingoX never holds your funds — your broker does.
      </div>
      <button
        onClick={onOpen}
        data-testid="risk-banner-open"
        className="ml-auto shrink-0 font-mono text-[10px] uppercase tracking-[0.2em] text-amber-200 hover:text-white underline underline-offset-4"
      >
        Read full disclaimer
      </button>
    </div>
  </div>
);

export const FullRiskDisclaimer = () => (
  <div className="space-y-5 text-sm leading-relaxed text-slate-300">
    <section>
      <h3 className="font-display text-lg font-bold text-white flex items-center gap-2">
        <AlertTriangle className="w-4 h-4 text-amber-400" /> Trading involves substantial risk
      </h3>
      <p className="mt-2">
        Trading equities, options, ETFs, and other financial instruments is <b className="text-amber-300">highly speculative</b> and
        involves a substantial risk of loss. Past performance — simulated or real — is <b>not indicative</b> of future results.
        AI signals, technical indicators, and algorithmic strategies can and do fail. You may lose some, all, or more than your initial capital.
      </p>
    </section>

    <section>
      <h3 className="font-display text-base font-bold text-white">Only trade money you can afford to lose</h3>
      <ul className="mt-2 space-y-1.5 list-disc pl-5 marker:text-amber-400">
        <li>Never trade with money you cannot afford to lose completely.</li>
        <li><b>Do not borrow money</b> — credit cards, personal loans, margin, or otherwise — to fund your trading.</li>
        <li><b>Do not use your life savings, retirement, emergency, or rent/mortgage money.</b></li>
        <li>Consult a licensed financial advisor before trading if you are unsure whether trading is suitable for your circumstances.</li>
      </ul>
    </section>

    <section className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-4">
      <h3 className="font-display text-base font-bold text-emerald-200 flex items-center gap-2">
        <ShieldCheck className="w-4 h-4" /> TradingoX does <span className="underline">not</span> hold or handle your funds
      </h3>
      <ul className="mt-2 space-y-1.5 text-slate-200 list-disc pl-5 marker:text-emerald-400">
        <li>Your money is held exclusively by your regulated <b>broker</b> (e.g., Alpaca). We have <b>no access</b> to your bank account, your broker cash, or your positions beyond the <b>trading buying-power</b> you release to us via broker API keys.</li>
        <li>You decide how much capital your broker exposes to TradingoX — set the buying power / withdrawable balance <b>at your broker</b>. Reduce or revoke access at any time from your broker dashboard.</li>
        <li><b>All deposits, withdrawals, and profit payouts are handled entirely by your broker.</b> TradingoX never processes deposits or withdrawals and cannot move money on your behalf.</li>
        <li>You can disconnect the broker at any time from the <span className="font-mono">Broker</span> panel — this immediately stops the bot from placing any further orders.</li>
      </ul>
    </section>

    <section>
      <h3 className="font-display text-base font-bold text-white flex items-center gap-2">
        <Wallet className="w-4 h-4 text-slate-400" /> Paper trading vs. live trading
      </h3>
      <p className="mt-2">
        By default TradingoX runs in paper-trading mode with simulated capital. Paper results are not guaranteed to translate to live markets:
        slippage, spread, fills, latency, and liquidity behave differently with real money. Verify a strategy thoroughly in paper mode
        <i> and</i> understand its worst-case drawdown before enabling live mode at your broker.
      </p>
    </section>

    <section>
      <h3 className="font-display text-base font-bold text-white">No investment advice</h3>
      <p className="mt-2">
        Nothing on TradingoX is investment, financial, legal, or tax advice. Signals, rationales, and automated actions are provided
        for informational and educational purposes only. You are solely responsible for every trade routed through your broker,
        whether initiated manually or by the autonomous bot.
      </p>
    </section>

    <section>
      <h3 className="font-display text-base font-bold text-white">No guarantees. No liability.</h3>
      <p className="mt-2">
        TradingoX makes no representation that its signals are accurate, timely, or profitable. To the maximum extent permitted by law,
        TradingoX and its operators shall not be liable for any direct, indirect, incidental, consequential, or punitive damages arising
        from your use of the platform, including but not limited to trading losses, missed trades, connectivity issues, or API errors.
      </p>
    </section>

    <p className="text-[11px] text-slate-500 italic">
      By using TradingoX you acknowledge that you have read, understood, and accepted this disclaimer in full.
    </p>
  </div>
);

export const RiskFooter = () => (
  <footer className="relative z-10 mt-16 border-t border-white/5 bg-[#090A0F]/80">
    <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-8 text-xs text-slate-400 space-y-4">
      <div className="flex items-center gap-2 text-amber-200 font-semibold">
        <AlertTriangle className="w-4 h-4" /> High-risk investment warning
      </div>
      <p>
        <b>Trading is highly risky.</b> Only trade the money you have and can afford to lose. <b>Do not borrow money to trade.
        Do not use your life savings.</b> You may lose some, all, or more than your initial capital. Past or simulated performance
        is not a reliable indicator of future results.
      </p>
      <p>
        <b>Fund custody:</b> TradingoX does not hold, receive, or move your funds. Your money remains with your regulated broker.
        We only interact with the trading buying-power you release to us via broker API keys, which you can revoke at any time.
        <b> All deposits, withdrawals and profit payouts are handled entirely by your broker.</b>
      </p>
      <p className="text-slate-500">
        TradingoX is a technology platform, not a broker, exchange, or investment adviser. Nothing here is investment advice.
        Use of this site constitutes acceptance of the full risk disclaimer.
      </p>

      {/* Amanacare Ministries donation block */}
      <a
        href="https://www.zeffy.com/en-US/donation-form/donate-to-amanacare-ministries-change-lives"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="amanafest-support-link"
        className="group mt-6 flex flex-col sm:flex-row items-center gap-5 rounded-2xl border border-amber-300/20 bg-gradient-to-r from-[#0B1024] via-[#0D1330] to-[#0B1024] px-6 py-5 hover:border-amber-300/50 transition-colors"
      >
        <img
          src="/brand/amanafest-logo.png"
          alt="Amanafest · Fueled by Love"
          className="w-20 h-20 sm:w-24 sm:h-24 rounded-full ring-2 ring-amber-300/40 shadow-[0_0_35px_-8px_rgba(251,191,36,0.4)] group-hover:ring-amber-300/70 transition object-cover bg-[#0B1024]"
        />
        <div className="text-center sm:text-left flex-1">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-300">
            Supporting a higher purpose
          </div>
          <div className="mt-1 font-display text-lg font-bold text-slate-100">
            TradingoX supports <span className="text-amber-300">Amanafest</span>
          </div>
          <p className="mt-1 text-[13px] leading-relaxed text-slate-300 max-w-2xl">
            A ministry of <b>Amanacare Ministries</b>, a Christian nonprofit organization helping the <b>elderly</b>, <b>people with disabilities</b>, and <b>low-income families</b>. Please consider donating to help us continue this much-needed service.
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2 justify-center sm:justify-start">
            <span
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                window.open("https://www.zeffy.com/en-US/donation-form/donate-to-amanacare-ministries-change-lives", "_blank", "noopener,noreferrer");
              }}
              data-testid="amanafest-donate-btn"
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  e.stopPropagation();
                  window.open("https://www.zeffy.com/en-US/donation-form/donate-to-amanacare-ministries-change-lives", "_blank", "noopener,noreferrer");
                }
              }}
              className="inline-flex items-center gap-1.5 rounded-full bg-amber-300 hover:bg-amber-200 text-[#0B1024] text-[12px] font-bold px-4 py-1.5 shadow-[0_0_25px_-5px_rgba(251,191,36,0.6)] transition"
            >
              Donate now
              <span aria-hidden>♥</span>
            </span>
          </div>
        </div>
      </a>

      <div className="pt-4 mt-2 border-t border-white/5 text-center text-slate-500 space-y-1">
        <div className="flex flex-wrap items-center justify-center gap-2 mb-3">
          <a
            href="/contact"
            data-testid="footer-contact-link"
            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black font-semibold text-[12px] transition shadow-[0_6px_20px_-8px_rgba(16,185,129,0.6)]"
          >
            <MessageSquare className="w-3.5 h-3.5" />
            Contact Us
          </a>
          <a
            href="/faq"
            data-testid="footer-faq-link"
            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full border border-white/15 bg-white/5 hover:bg-white/10 hover:border-emerald-400/40 text-slate-200 hover:text-white text-[12px] font-semibold transition"
          >
            <HelpCircle className="w-3.5 h-3.5 text-emerald-300" />
            FAQs
          </a>
          <a
            href="/training"
            data-testid="footer-training-link"
            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full border border-white/15 bg-white/5 hover:bg-white/10 hover:border-emerald-400/40 text-slate-200 hover:text-white text-[12px] font-semibold transition"
          >
            <GraduationCap className="w-3.5 h-3.5 text-emerald-300" />
            Training
          </a>
          <a
            href="sms:+19013619173"
            data-testid="footer-sms-link"
            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full border border-emerald-400/30 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300 hover:text-emerald-200 text-[12px] font-semibold transition"
          >
            <Phone className="w-3.5 h-3.5" />
            Text 901-361-9173
          </a>
        </div>
        <div>
          TradingoX © {new Date().getFullYear()} · All rights reserved · Owned and operated by <span className="text-slate-300 font-semibold">XYSCOTECH</span>, an <span className="text-slate-300 font-semibold">IXYSCO LLC</span> company.
        </div>
        <div>
          Springfield, Missouri
        </div>
        <div>
          Designed and developed by <span className="text-slate-300">Charles Cawv Thao</span>
        </div>
        <div className="text-[11px] text-slate-600">
          AI scalping terminal · Not a broker · Not investment advice · Trade at your own risk ·{" "}
          <a
            href="/terms"
            data-testid="footer-terms-link"
            className="text-slate-500 hover:text-emerald-300 underline underline-offset-2"
          >
            Terms &amp; Disclaimer
          </a>
          {" · "}
          <a
            href="/admin"
            data-testid="footer-admin-link"
            className="text-slate-600 hover:text-emerald-300 underline underline-offset-2 font-mono uppercase tracking-[0.15em] text-[10px]"
          >
            Admin
          </a>
        </div>
      </div>
    </div>
  </footer>
);

export const RiskDisclaimerModal = ({ open, onOpenChange, onAcknowledge, forceAck = false }) => {
  const close = () => {
    if (forceAck) return;
    onOpenChange?.(false);
  };
  return (
    <Dialog open={open} onOpenChange={forceAck ? () => {} : onOpenChange}>
      <DialogContent
        data-testid="risk-disclaimer-modal"
        className="bg-[#12141D] border-white/10 text-slate-100 max-w-2xl max-h-[85vh] overflow-y-auto aq-scroll"
        onEscapeKeyDown={(e) => forceAck && e.preventDefault()}
        onPointerDownOutside={(e) => forceAck && e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-extrabold text-white flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-400" /> Risk & fund-custody disclaimer
          </DialogTitle>
        </DialogHeader>
        <FullRiskDisclaimer />
        <div className="flex justify-end gap-2 pt-2">
          {!forceAck && (
            <Button variant="ghost" onClick={close} className="text-slate-300">
              <X className="w-4 h-4 mr-1" /> Close
            </Button>
          )}
          {onAcknowledge && (
            <Button
              data-testid="risk-accept-button"
              onClick={() => { onAcknowledge(); onOpenChange?.(false); }}
              className="bg-amber-500 hover:bg-amber-400 text-black font-semibold rounded-full h-10 px-5"
            >
              I understand and accept the risks
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export function useRiskAck() {
  const [ack, setAck] = useState(() => {
    try { return typeof window !== "undefined" && window.localStorage.getItem(ACCEPT_KEY) === "1"; }
    catch { return false; }
  });
  const [open, setOpen] = useState(false);
  useEffect(() => { if (!ack) setOpen(true); }, [ack]);
  const accept = () => {
    try { window.localStorage.setItem(ACCEPT_KEY, "1"); } catch { /* ignore */ }
    setAck(true);
  };
  return { ack, open, setOpen, accept };
}
